import type {
  QuestionRadiogroupModelData,
  SurveyAnswerRecord,
  SurveyAnswerValue,
} from '@coorum-research/shared-common/types'
import type { SurveyModel } from 'survey-core'
import type { SelectRadiogroupOption } from '../../utils'
import type { SurveyElementFormHandler } from './survey-element-form'
import { ValidationErrorMessages } from '@coorum-research/shared-common/shared/survey/survey-validation'
import { generateRadiogroupSelectOption } from '../../utils'

export class SurveyElementFormHandlerRadiogroup implements SurveyElementFormHandler {
  readonly #elementJson: QuestionRadiogroupModelData
  #elementResultRecord: SurveyAnswerRecord | null
  #isAnswerOtherValue: boolean = false

  constructor(
    elementJson: QuestionRadiogroupModelData,
    elementResultRecord: SurveyAnswerRecord | null,
  ) {
    this.#elementJson = elementJson
    this.#elementResultRecord = elementResultRecord
  }

  get elementJson(): QuestionRadiogroupModelData {
    return this.#elementJson
  }

  get surveyModel(): SurveyModel | null {
    return null
  }

  get data(): SurveyAnswerRecord {
    return this.#elementResultRecord || {}
  }

  getValue(): SurveyAnswerValue {
    return this.#elementResultRecord?.[this.#elementJson.name] ?? ''
  }

  getComment(): string {
    return this.#elementResultRecord?.[`${this.#elementJson.name}-Comment`] as string || ''
  }

  _hasSelectOptionOtherValue(): boolean {
    return this.#elementJson.showOtherItem !== undefined ? this.#elementJson.showOtherItem : false
  }

  getSelectOptions(): SelectRadiogroupOption[] {
    return generateRadiogroupSelectOption(this.#elementJson)
  }

  getSelectOptionValue(): SurveyAnswerValue {
    const originalValue = this.getValue()
    if (!this._hasSelectOptionOtherValue()) {
      return originalValue
    }

    // falsy(empty value)なら、そのまま帰す
    if (!String(originalValue)) {
      return ''
    }

    // originalValue値が存在する場合
    // originalValueとselectOptions#valuewoを比較して、一致するのがなければ 'other'と判定する
    const matchedOptions = this.getSelectOptions().filter(option => option.value === originalValue)
    if (matchedOptions.length > 0) {
      // 一致する値がある場合は、そのままoriginalValueを返す
      return originalValue
    }

    return 'other' // 一致する値がない場合は 'other' と判定
  }

  /**
   *
   * 選択で「その他（説明）」を選択するとvalueは'other'になる。
   * 'other' の場合には、それをそのままvalueに使うのではなく、説明テキストをvalueとして保存する（surveyjs仕様に従う）
   * 'other' を選択した場合には、設問が必須設定の有無に関係なく otherTextを必須となる。
   */
  setElementResultRecordValue(value: SurveyAnswerValue) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    if (value === 'other') {
      // 'other' を選択した場合は、valueは'other'ではなく、説明テキストを保存するので、選択肢の変更時にはemptyにする
      this.#elementResultRecord[this.#elementJson.name] = ''
      this.#isAnswerOtherValue = true // for validation
    } else {
      this.#elementResultRecord[this.#elementJson.name] = value
      this.#isAnswerOtherValue = false // for validation
    }
  }

  /**
   * 'other' の説明テキスト保存
   */
  setElementResultRecordOtherValue(otherText: string) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    this.#elementResultRecord[this.#elementJson.name] = otherText || ''
  }

  setElementResultRecordComment(comment: string) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    this.#elementResultRecord[`${this.#elementJson.name}-Comment`] = comment || ''
  }

  /**
   * 'other' を選択した場合には、設問が必須設定の有無に関係なく otherTextlを必須となる。
   */
  validate(): {
    isValid: boolean
    errorMessage?: string
  } {
    const element = this.#elementJson
    const value = this.getValue()

    // otherを選択した際にはotherTextの入力は必須
    if (this.#isAnswerOtherValue && !value) {
      return {
        isValid: false,
        errorMessage: ValidationErrorMessages.OTHER_REQUIRED,
      }
    }

    // 必須チェック
    if (element.isRequired && (value === null || value === undefined || value === '')) {
      return {
        isValid: false,
        errorMessage: ValidationErrorMessages.REQUIRED,
      }
    }

    return {
      isValid: true,
    }
  }
}
